import { createReducer, createActions } from 'reduxsauce'
import {
  failureFeedback,
  initialFeedback,
  requestFeedback,
  successFeedback
} from './utils'
import {
  saveGeneratedDocuments,
  loadFiscalProgress,
  clearFiscalProgress,
  updateFiscalProgress
} from '../utils/fiscalProgressStorage'

// Load persisted progress on initialization
const getInitialState = () => {
  const persistedProgress = loadFiscalProgress()
  return {
    documents: (persistedProgress && persistedProgress.documents) || {},
    feedback: initialFeedback(),
    acceptanceFeedback: initialFeedback(),
    permissionCheckFeedback: initialFeedback(),
    shouldShowModal: false,
    // Add progress tracking fields
    acceptedDocuments: (persistedProgress && persistedProgress.acceptedDocuments) || [],
    currentDocumentIndex: (persistedProgress && persistedProgress.currentDocumentIndex) || 0
  }
}

const INITIAL_STATE = getInitialState()

const { Types, Creators } = createActions(
  {
    checkFiscalPermissions: [],
    checkFiscalPermissionsSuccess: ['data'],
    checkFiscalPermissionsFailure: ['errors'],
    generateInitialDocuments: ['data'],
    generateInitialDocumentsSuccess: ['data'],
    generateInitialDocumentsFailure: ['errors'],
    acceptContracts: [],
    acceptContractsSuccess: ['data'],
    acceptContractsFailure: ['errors'],
    acceptDocument: ['documentKey', 'currentIndex'],
    updateDocumentProgress: ['currentIndex', 'acceptedDocuments'],
    closeFiscalModal: [],
    clear: [],
  },
  { prefix: 'FISCAL_INFO_' }
)

const generateInitialDocuments = (state, { data }) => ({
  ...state,
  feedback: requestFeedback()
})

const generateInitialDocumentsSuccess = (state, { data }) => {
  // Persist generated documents to SessionStorage
  saveGeneratedDocuments(data)
  
  return {
    ...state,
    documents: data,
    feedback: successFeedback()
  }
}

const generateInitialDocumentsFailure = (state, { errors }) => ({
  ...state,
  feedback: failureFeedback(errors)
})

const acceptContracts = (state) => ({
  ...state,
  acceptanceFeedback: requestFeedback()
})

const acceptContractsSuccess = (state) => ({
  // Don't clear progress here - let the user see the completion message first
  // Progress will be cleared when the modal is closed manually
  ...state,
  acceptanceFeedback: successFeedback(),
  // Keep acceptedDocuments and currentDocumentIndex to show completion
  // They will be cleared when the modal closes
})

const acceptContractsFailure = (state, { errors }) => ({
  ...state,
  acceptanceFeedback: failureFeedback(errors)
})

const checkFiscalPermissions = (state) => ({
  ...state,
  permissionCheckFeedback: requestFeedback()
})

const checkFiscalPermissionsSuccess = (state, { data }) => {
  // If documents exist, save them to progress
  if (data && Object.keys(data).length > 0) {
    saveGeneratedDocuments(data)
  }
  
  return {
    ...state,
    permissionCheckFeedback: successFeedback(),
    shouldShowModal: false,
    documents: data
  }
}

const checkFiscalPermissionsFailure = (state, { errors }) => ({
  ...state,
  permissionCheckFeedback: failureFeedback(errors),
  shouldShowModal: true 
})

const closeFiscalModal = (state) => ({
  ...state,
  shouldShowModal: false
})

const acceptDocument = (state, { documentKey, currentIndex }) => {
  const newAcceptedDocuments = [...state.acceptedDocuments]
  if (!newAcceptedDocuments.includes(documentKey)) {
    newAcceptedDocuments.push(documentKey)
  }
  
  // Update progress in SessionStorage
  updateFiscalProgress({
    acceptedDocuments: newAcceptedDocuments,
    currentDocumentIndex: currentIndex
  })
  
  return {
    ...state,
    acceptedDocuments: newAcceptedDocuments,
    currentDocumentIndex: currentIndex
  }
}

const updateDocumentProgress = (state, { currentIndex, acceptedDocuments }) => {
  // Update progress in SessionStorage
  updateFiscalProgress({
    acceptedDocuments,
    currentDocumentIndex: currentIndex
  })
  
  return {
    ...state,
    acceptedDocuments,
    currentDocumentIndex: currentIndex
  }
}

const clear = () => {
  // Clear persisted progress when clearing state
  clearFiscalProgress()
  return getInitialState()
}

export const FiscalInfoActionTypes = Types

const HANDLERS = {
  [Types.CHECK_FISCAL_PERMISSIONS]: checkFiscalPermissions,
  [Types.CHECK_FISCAL_PERMISSIONS_SUCCESS]: checkFiscalPermissionsSuccess,
  [Types.CHECK_FISCAL_PERMISSIONS_FAILURE]: checkFiscalPermissionsFailure,
  [Types.GENERATE_INITIAL_DOCUMENTS]: generateInitialDocuments,
  [Types.GENERATE_INITIAL_DOCUMENTS_SUCCESS]: generateInitialDocumentsSuccess,
  [Types.GENERATE_INITIAL_DOCUMENTS_FAILURE]: generateInitialDocumentsFailure,
  [Types.ACCEPT_CONTRACTS]: acceptContracts,
  [Types.ACCEPT_CONTRACTS_SUCCESS]: acceptContractsSuccess,
  [Types.ACCEPT_CONTRACTS_FAILURE]: acceptContractsFailure,
  [Types.ACCEPT_DOCUMENT]: acceptDocument,
  [Types.UPDATE_DOCUMENT_PROGRESS]: updateDocumentProgress,
  [Types.CLOSE_FISCAL_MODAL]: closeFiscalModal,
  [Types.CLEAR]: clear
}

export const FiscalInfoActionCreators = Creators

export default createReducer(INITIAL_STATE, HANDLERS)
